'use client';

import React, { useEffect, useRef, useState } from 'react';
import { cn } from '@/core/utils/cn';

interface AnimatedWebpPlayerProps {
  src: string;
  alt?: string;
  speed?: number;
  frameDurationMs?: number;
  endPauseMs?: number;
  pauseOnHover?: boolean;
  loop?: boolean;
  style?: React.CSSProperties;
  className?: string;
  fallbackElement: React.ReactNode;
}

interface DecodedFrame {
  bitmap: ImageBitmap;
  durationMs: number;
}

export function AnimatedWebpPlayer({
  src,
  alt,
  speed = 1,
  frameDurationMs,
  endPauseMs,
  pauseOnHover = false,
  loop = true,
  style,
  className,
  fallbackElement,
}: AnimatedWebpPlayerProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [frames, setFrames] = useState<DecodedFrame[] | null>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [decodeError, setDecodeError] = useState(false);
  const currentFrameIndexRef = useRef(0);
  const animationTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Decode frames when src changes
  useEffect(() => {
    let isMounted = true;
    setFrames(null);
    setDecodeError(false);
    currentFrameIndexRef.current = 0;

    if (!src) {
      setDecodeError(true);
      return;
    }

    // Check if ImageDecoder is available in browser
    if (typeof window === 'undefined' || !(window as any).ImageDecoder) {
      setDecodeError(true);
      return;
    }

    const loadAndDecode = async () => {
      try {
        const resolvedSrc = src.startsWith('http') || src.startsWith('/') || src.startsWith('data:') || src.startsWith('blob:') ? src : `/${src}`;
        const response = await fetch(resolvedSrc);
        if (!response.ok) throw new Error(`HTTP error ${response.status}`);
        const buffer = await response.arrayBuffer();

        // Determine mime type
        const lower = src.toLowerCase();
        const type = lower.includes('.gif') ? 'image/gif' : 'image/webp';

        const decoder = new (window as any).ImageDecoder({ data: buffer, type });
        await decoder.tracks.ready;

        const decodedList: DecodedFrame[] = [];
        let i = 0;

        while (isMounted) {
          try {
            const res = await decoder.decode({ frameIndex: i });
            const bitmap = await createImageBitmap(res.image);
            // Duration is in microseconds in WebCodecs API
            const dur = res.image.duration ? res.image.duration / 1000 : 100;
            res.image.close();

            decodedList.push({
              bitmap,
              durationMs: dur > 0 ? dur : 100,
            });

            i++;
            if (decoder.complete && decoder.tracks.selectedTrack && i >= decoder.tracks.selectedTrack.frameCount) {
              break;
            }
          } catch {
            // End of stream or frame decode finished
            break;
          }
        }

        if (frameCountValid(decodedList) && isMounted) {
          setFrames(decodedList);
        } else {
          if (isMounted) setDecodeError(true);
        }
      } catch (err) {
        console.warn('AnimatedWebpPlayer decode notice:', err);
        if (isMounted) setDecodeError(true);
      }
    };

    function frameCountValid(list: DecodedFrame[]) {
      return list && list.length > 1;
    }

    loadAndDecode();

    return () => {
      isMounted = false;
      if (animationTimerRef.current) {
        clearTimeout(animationTimerRef.current);
      }
    };
  }, [src]);

  // Frame animation playback loop
  useEffect(() => {
    if (!frames || frames.length <= 1 || decodeError) return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    if (animationTimerRef.current) {
      clearTimeout(animationTimerRef.current);
    }

    const renderCurrentFrame = () => {
      const idx = currentFrameIndexRef.current;
      const frame = frames[idx];
      if (!frame) return;

      if (canvas.width !== frame.bitmap.width || canvas.height !== frame.bitmap.height) {
        canvas.width = frame.bitmap.width;
        canvas.height = frame.bitmap.height;
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(frame.bitmap, 0, 0);

      // Determine next frame interval
      const effectiveSpeed = Math.max(0.1, Number(speed) || 1);
      let nextDuration = frameDurationMs ? Number(frameDurationMs) : (frame.durationMs / effectiveSpeed);

      const isLastFrame = idx === frames.length - 1;
      if (isLastFrame && endPauseMs !== undefined && endPauseMs > 0) {
        nextDuration = Number(endPauseMs);
      }

      if (nextDuration < 16) nextDuration = 16; // minimum 60fps cap

      if (pauseOnHover && isHovered) {
        // Paused on hover: recheck in 100ms without advancing frame
        animationTimerRef.current = setTimeout(renderCurrentFrame, 100);
        return;
      }

      let nextIndex = idx + 1;
      if (nextIndex >= frames.length) {
        if (!loop) return;
        nextIndex = 0;
      }
      currentFrameIndexRef.current = nextIndex;

      animationTimerRef.current = setTimeout(renderCurrentFrame, nextDuration);
    };

    renderCurrentFrame();

    return () => {
      if (animationTimerRef.current) {
        clearTimeout(animationTimerRef.current);
      }
    };
  }, [frames, speed, frameDurationMs, endPauseMs, pauseOnHover, isHovered, loop, decodeError]);

  if (decodeError || !frames) {
    return <>{fallbackElement}</>;
  }

  return (
    <canvas
      ref={canvasRef}
      role="img"
      aria-label={alt || 'Animacja'}
      style={{
        width: '100%',
        height: '100%',
        objectFit: style?.objectFit || 'cover',
        objectPosition: style?.objectPosition || 'center center',
        ...style,
      }}
      className={cn(className)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    />
  );
}
